import { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  useColorMode,
  Flex,
  Text,
  Heading,
  Center,
  VStack,
} from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { SWRConfig } from "swr";
import { useTranslation } from "react-i18next";
import CreateBook from "./components/CreateBook";
import Library from "./components/Library";
import MyBooks from "./components/MyBooks";
import UpdateBook from "./components/UpdateBook";
import ReadBook from "./components/ReadBook";
import Menu from "./components/Menu";
import About from "./components/About";
import ProcessDocument from "./components/ProcessDocument";
import Footer from "./components/Footer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfUse from "./components/TermsOfUse";
import Login from "./components/Login";
import DocumentIntro from "./components/DocumentIntro";
import BooksIntro from "./components/BooksIntro";
import { Language } from "./i18n/i18n";
import SpinnerProvider from "./components/SpinnerProvider";
import { useAuthStore } from "./store/authStore";
import { useLanguageStore } from "./store/languageStore";
import Error from "./components/Error";

// Disabled feature message component
const DisabledFeature = () => {
  const { t } = useTranslation();
  return (
    <Center h="60vh">
      <VStack spacing={4} textAlign="center" p={8}>
        <Heading size="lg">Feature Temporarily Disabled</Heading>
        <Text>
          The ability to create new books has been temporarily disabled.
        </Text>
        <Text>
          Please check back later or contact the administrator for more
          information.
        </Text>
      </VStack>
    </Center>
  );
};

function App() {
  const { colorMode, toggleColorMode } = useColorMode();
  const { i18n, t } = useTranslation();
  const { checkAuth, userType, isAuthenticated } = useAuthStore();
  const hasSetInitialColorMode = useRef(false);

  // Ensure auth state is up to date on load
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Start in dark mode by default (only on first mount, so the toggle can switch to light).
  useEffect(() => {
    if (hasSetInitialColorMode.current) return;
    hasSetInitialColorMode.current = true;
    if (colorMode === "light") {
      toggleColorMode();
    }
  }, [colorMode, toggleColorMode]);

  const canCreate =
    isAuthenticated && userType && ["contributor", "admin"].includes(userType);

  // Apply persisted language when language store rehydrates from localStorage
  useEffect(() => {
    const applyPersistedLanguage = () => {
      const lang = useLanguageStore.getState().language;
      if (lang) {
        i18n.changeLanguage(lang);
      }
    };
    if (useLanguageStore.persist.hasHydrated()) {
      applyPersistedLanguage();
    }
    const unsub = useLanguageStore.persist.onFinishHydration(
      applyPersistedLanguage,
    );
    return unsub;
  }, []);

  // Normalize language to a supported code when no persisted choice (e.g. first visit)
  useEffect(() => {
    const currentLang = i18n.language || "en";
    let normalizedLang =
      currentLang === "kk-Latn" ? Language.KAZAKH_LATIN : currentLang;
    if (
      normalizedLang.startsWith("kk") &&
      normalizedLang !== Language.KAZAKH_CYRILLIC &&
      normalizedLang !== Language.KAZAKH_LATIN
    ) {
      normalizedLang =
        normalizedLang.toLowerCase().includes("latin") ||
        normalizedLang.toLowerCase().includes("latn")
          ? Language.KAZAKH_LATIN
          : Language.KAZAKH_CYRILLIC;
    } else if (
      normalizedLang.startsWith("ru") &&
      normalizedLang !== Language.RUSSIAN
    ) {
      normalizedLang = Language.RUSSIAN;
    } else if (
      normalizedLang.startsWith("en") &&
      normalizedLang !== Language.ENGLISH
    ) {
      normalizedLang = Language.ENGLISH;
    }
    if (normalizedLang !== currentLang) {
      useLanguageStore.getState().setLanguage(normalizedLang);
    }
  }, [i18n]);

  return (
    <SWRConfig
      value={{
        revalidateOnFocus: true,
        dedupingInterval: 2000,
      }}
    >
      <SpinnerProvider>
        <Router>
          <Flex
            minH="100vh"
            direction="column"
            bg={colorMode === "light" ? "gray.50" : "gray.800"}
            transition="background-color 0.2s"
          >
            <Container
              maxW="container.lg"
              px={{ base: 6, md: 4 }}
              py={4}
              flex="1"
            >
              <Routes>
                <Route
                  path="/"
                  element={
                    <>
                      <Menu />
                      <Box pt={4}>
                        <Library />
                      </Box>
                    </>
                  }
                />
                <Route
                  path="/create"
                  element={
                    isAuthenticated ? (
                      canCreate ? (
                        <>
                          <Menu />
                          <Box pt={4}>
                            <CreateBook />
                          </Box>
                        </>
                      ) : (
                        <>
                          <Menu />
                          <Box pt={4}>
                            <Error
                              type="unauthorized"
                              title={t("createBook.notAuthorizedTitle")}
                              message={t("createBook.notAuthorizedMessage")}
                            />
                          </Box>
                        </>
                      )
                    ) : (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <VStack align="stretch" spacing={6}>
                            <BooksIntro />
                            <Login redirectTo="/create" />
                          </VStack>
                        </Box>
                      </>
                    )
                  }
                />
                <Route
                  path="/my-books"
                  element={
                    isAuthenticated ? (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <MyBooks />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <VStack align="stretch" spacing={6}>
                            <BooksIntro />
                            <Login redirectTo="/my-books" />
                          </VStack>
                        </Box>
                      </>
                    )
                  }
                />
                <Route
                  path="/update-book/:id"
                  element={
                    isAuthenticated ? (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <UpdateBook />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <Login redirectTo="/my-books" />
                        </Box>
                      </>
                    )
                  }
                />
                <Route
                  path="/document"
                  element={
                    isAuthenticated ? (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <ProcessDocument />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Menu />
                        <Box pt={4}>
                          <DocumentIntro />
                        </Box>
                      </>
                    )
                  }
                />
                <Route
                  path="/about"
                  element={
                    <>
                      <Menu />
                      <Box pt={4}>
                        <About />
                      </Box>
                    </>
                  }
                />
                <Route path="/read/:id/:chapterId" element={<ReadBook />} />
                <Route path="/read/:id" element={<ReadBook />} />
                <Route
                  path="/login"
                  element={
                    <>
                      <Menu />
                      <Box pt={4}>
                        <Login />
                      </Box>
                    </>
                  }
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
              </Routes>
            </Container>
            <Footer />
          </Flex>
        </Router>
      </SpinnerProvider>
    </SWRConfig>
  );
}

export default App;
